import {
    attr,
    css,
    type ElementStyles,
    FASTElement,
    html,
    observable,
    ref,
    type ViewTemplate,
    volatile,
} from '@microsoft/fast-element';
import loc, { type ResourceId } from 'owa-localize';
import type { HoverActionKey } from 'owa-outlook-service-option-store';
import { PinUnpin } from './PinUnpinHoverIcon';
import { ReadUnread } from './ReadUnreadHoverIcon';
import { FlagUnflag } from './FlagUnflagHoverIcon';
import { Archive } from './ArchiveHoverIcon';
import { Delete } from './DeleteHoverIcon';
import { Settings } from './SettingsHoverIcon';
import { iconBarRegistry } from '../customElementRegistry';
import { Move } from './MoveHoverIcon';
import type { MailboxInfo } from 'owa-client-types';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import getMailboxInfo from 'owa-mail-mailboxinfo/lib/getMailboxInfo';
import { type TableView } from 'owa-mail-list-store';

export const hoverIconTemplate: ViewTemplate<HoverIcon> = html<HoverIcon>` <template
    tabIndex="-1"
    aria-label="${x => loc(x.titleLocString)}"
    title="${x => loc(x.titleLocString)}"
    @click="${(x, c) => {
        c.event.stopPropagation();
        x.onClick?.(c.event as MouseEvent);
    }}"
    @dblclick="${(_, c) => c.event.stopPropagation()}"
    @contextmenu="${(x, c) => x.onContextMenu?.(c.event as MouseEvent)}"
>
    <span
        class="icon"
        ${
            // eslint-disable-next-line owa-custom-rules/no-side-effects -- web components
            ref('iconRef')
        }
    ></span>
    ${x => x.customTemplate}
</template>`;

export const baseHoverIconStyles: ElementStyles = css`
    :host {
        align-items: center;
        background-color: transparent;
        border-width: 0px;
        color: var(--neutralPrimaryAlt);
        cursor: pointer;
        display: var(--hoverActionIconDisplay, none);
        font-family: FluentSystemIcons;
        font-size: 16px;
        font-weight: 100;
        height: 24px;
        justify-content: center;
        margin: 0px;
        padding-left: 0px;
        text-align: center;
        width: 24px;
    }

    :host(.full) {
        font-size: 20px;
    }

    :host(.iconDisplay) {
        display: inline-flex;
    }

    :host(.iconHide) {
        visibility: var(--iconVisibilityWhenDefaultHidden, hidden);
    }
`;

const NoneHoverIconStyles: ElementStyles = css`
    :host {
        display: none;
    }
`;

export interface HoverIconDefinition<Instance extends HoverIcon = HoverIcon> {
    title(this: Readonly<Instance>): ResourceId;
    customCss?: ElementStyles;
    connectedCallback?(this: Readonly<Instance>): void;
    disconnectedCallback?(this: Readonly<Instance>): void;
    onClick?(this: Readonly<Instance>, evt: MouseEvent): void;
    onContextMenu?(this: Readonly<Instance>, evt: MouseEvent): void;
    customTemplate?: ViewTemplate<Instance>;
}

const hoverIconDefinitions: Partial<Record<HoverActionKey, HoverIconDefinition>> & {
    None: HoverIconDefinition;
} = {
    None: {
        title: () => '',
        customCss: NoneHoverIconStyles,
    },
    PinUnpin,
    ReadUnread,
    FlagUnflag,
    Archive,
    Delete,
    Settings,
    Move,
};

function getHoverIconDefinition(key: HoverActionKey): HoverIconDefinition {
    return hoverIconDefinitions[key] ?? hoverIconDefinitions['None'];
}

export class HoverIcon extends FASTElement {
    @attr
    actionKey!: HoverActionKey;

    // eslint-disable-next-line owa-custom-rules/no-side-effects -- Web components
    @attr({ mode: 'boolean' })
    isPinned!: boolean;

    // eslint-disable-next-line owa-custom-rules/no-side-effects -- Web components
    @attr({ mode: 'boolean' })
    hasUnread!: boolean;

    // eslint-disable-next-line owa-custom-rules/no-side-effects -- Web components
    @attr({ mode: 'boolean' })
    isComplete!: boolean;

    // eslint-disable-next-line owa-custom-rules/no-side-effects -- Web components
    @attr({ mode: 'boolean' })
    isFlagged!: boolean;

    @attr
    rowKey!: string;

    @attr
    tableViewId!: string;

    @volatile
    get tableView(): TableView | undefined {
        return getListViewStore().tableViews.get(this.tableViewId);
    }

    @volatile
    get mailboxInfo(): MailboxInfo {
        return getMailboxInfo(this.tableView);
    }

    @volatile
    private get actionDefinition(): HoverIconDefinition {
        return getHoverIconDefinition(this.actionKey);
    }

    @volatile
    get titleLocString(): ResourceId {
        return this.actionDefinition.title.call(this);
    }

    @volatile
    get onClick(): ((evt: MouseEvent) => void) | undefined {
        return this.actionDefinition.onClick?.bind(this);
    }

    @volatile
    get onContextMenu(): ((evt: MouseEvent) => void) | undefined {
        return this.actionDefinition.onContextMenu?.bind(this);
    }

    @volatile
    get customTemplate(): ViewTemplate<HoverIcon> | undefined {
        return this.actionDefinition.customTemplate;
    }

    @observable
    iconRef!: HTMLSpanElement;

    actionKeyChanged(oldValue: HoverActionKey, newValue: HoverActionKey): void {
        const oldActionDefinition = getHoverIconDefinition(oldValue);
        oldActionDefinition.disconnectedCallback?.call(this);
        this.$fastController.removeStyles(oldActionDefinition.customCss);

        const newActionDefinition = getHoverIconDefinition(newValue);
        this.$fastController.addStyles(newActionDefinition.customCss);
        newActionDefinition.connectedCallback?.call(this);
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.$fastController.addStyles(this.actionDefinition.customCss);
        this.actionDefinition.connectedCallback?.call(this);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.actionDefinition.disconnectedCallback?.call(this);
        this.$fastController.removeStyles(this.actionDefinition.customCss);
    }
}

// eslint-disable-next-line owa-custom-rules/no-side-effects -- Web components
HoverIcon.define({
    name: 'hover-icon',
    template: hoverIconTemplate,
    styles: baseHoverIconStyles,
    registry: iconBarRegistry,
});

declare global {
    namespace JSX {
        interface IntrinsicElements {
            'hover-icon': React.DetailedHTMLProps<React.HTMLProps<HTMLElement>, HoverIcon> & {
                actionKey: HoverActionKey;
                class?: string;
                isPinned: boolean;
                isComplete: boolean;
                isFlagged: boolean;
                hasUnread: boolean;
                rowKey: string;
                tableViewId: string;
            };
        }
    }
}
